import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/PageHero';
import { getImageUrl } from '../lib/utils';

const Colleges = () => {
  const [colleges, setColleges] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/colleges/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json())
    ])
      .then(([collegesData, settingsData]) => {
        setColleges(collegesData);
        setSettings(settingsData);
      })
      .catch(err => console.error('Error fetching data:', err))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <PageHero 
        title="الكليات"
        subtitle="تعرف على كليات الجامعة المتخصصة في العلوم الطبية"
        backgroundImage={getImageUrl(settings?.page_colleges_bg)}
      />

      {/* Colleges Grid */}
      <section className="py-20 bg-gray-50 dark:bg-[#071326]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {colleges.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">لا توجد كليات حالياً</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {colleges.map((college) => (
                <Card key={college.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 dark:bg-[#0E2243] dark:border-gray-800">
                  <div className="flex flex-col md:flex-row">
                    <div className="relative w-full md:w-1/3 h-64 md:h-auto overflow-hidden">
                      <img
                        src={getImageUrl(college.image, 'https://images.unsplash.com/photo-1519452575417-564c1401ecc0?w=800')}
                        alt={college.name_ar}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="flex-1 p-6">
                      <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-3 group-hover:text-[#C9A040] transition-colors">
                        {college.name_ar}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">{college.description_ar}</p>
                      {college.dean_name_ar && (
                        <div className="flex items-center gap-3 mb-4 bg-blue-50/70 dark:bg-gray-800/60 p-2.5 rounded-lg border border-blue-100/50 dark:border-gray-700">
                          {college.dean_image && (
                            <img
                              src={getImageUrl(college.dean_image)}
                              alt={college.dean_name_ar}
                              className="w-11 h-11 rounded-full object-cover border-2 border-[#C9A040] shadow-sm flex-shrink-0"
                            />
                          )}
                          <div className="text-xs">
                            <div className="text-gray-500 dark:text-gray-400 font-medium">عميد الكلية:</div>
                            <div className="font-bold text-[#0F388E] dark:text-gray-200">{college.dean_name_ar}</div>
                          </div>
                        </div>
                      )}
                      <Link to={`/colleges/${college.id}`}>
                        <Button variant="link" className="text-[#C9A040] hover:text-[#0F388E] dark:hover:text-[#FACC15] p-0 font-medium">
                          المزيد عن الكلية
                          <ArrowLeft className="mr-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Colleges;
